import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box,
    CardActions,
    CircularProgress,
    Divider,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    styled
} from '@mui/material';

import DialogWrapper from '@libComponents/Dialog';
import { Button } from '@libComponents';
import { setSnack } from 'utilities/redux.actions';
import { normalizeIso8601Date } from '@libs/dateTime';

import { exportTimesheetXlsx } from '../../utils/timesheetExcel';
import { getTimesheetExportContext } from '../../Timesheet/exportContext';
import {
    SelectBranch,
    TimesheetsMonthPicker,
    TimesheetsYearPicker
} from '..';

const StyledCardAction = styled(CardActions)(({ theme: { spacing } }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: spacing(3),
    paddingBottom: 0
}));

const GroupLabel = ({ children }) => (
    <Typography
        variant='subtitle2'
        sx={{ fontWeight: 600, mt: 2, mb: 0.75, textAlign: 'left' }}
    >
        {children}
    </Typography>
);

/**
 * Attendance Excel export. Uses the same attendance APIs and the same
 * per-day status resolution the timesheet views render, so the exported
 * statuses (present/remote/late/leave/holiday/weekend/absent) match the
 * screen exactly.
 *
 *   Monthly — every employee (optional branch / email filter).
 *   Yearly  — one employee (email required, mirroring the yearly view).
 *
 * When opened, it pre-fills from the ACTIVE tab's filters (month/year,
 * branch, email search) via the export context registry — so the export
 * matches what is on screen, minus pagination (all pages are fetched).
 */
const TimesheetExportModal = ({ open, onClose, view }) => {
    const now = new Date();
    const [period, setPeriod] = useState('monthly');
    const [monthDate, setMonthDate] = useState(
        `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    );
    const [yearDate, setYearDate] = useState(`${now.getFullYear()}-01-01`);
    const [email, setEmail] = useState('');
    const [branch, setBranch] = useState('');
    const [exporting, setExporting] = useState(false);
    const [progress, setProgress] = useState('');

    const dispatch = useDispatch();

    // Pre-fill from the active tab's filters each time the modal opens.
    useEffect(() => {
        if (!open) return;
        if (view === 'yearly') {
            setPeriod('yearly');
            const ctx = getTimesheetExportContext('yearly');
            if (ctx) {
                if (ctx.year) setYearDate(`${ctx.year}-01-01`);
                setEmail(ctx.email || '');
            }
        } else if (view === 'monthly') {
            setPeriod('monthly');
            const ctx = getTimesheetExportContext('monthly');
            if (ctx) {
                if (ctx.year && ctx.month) {
                    setMonthDate(
                        `${ctx.year}-${String(ctx.month).padStart(2, '0')}`
                    );
                }
                setEmail(ctx.email || '');
                setBranch(ctx.branch || '');
            }
        }
        // daily/weekly tabs: keep the defaults (current month, no filters)
    }, [open, view]);

    const emailRequired = period === 'yearly';
    const canExport = !exporting && (!emailRequired || email.trim());

    const handleClose = () => {
        if (!exporting) {
            setProgress('');
            onClose();
        }
    };

    const handleMonthChange = (value) => {
        try {
            if (!value) return;
            const normalized = normalizeIso8601Date(value);
            if (!normalized.includes('Invalid Date')) setMonthDate(normalized);
        } catch {}
    };

    const handleYearChange = (value) => {
        try {
            if (!value) return;
            const normalized = normalizeIso8601Date(value);
            if (!normalized.includes('Invalid Date')) setYearDate(normalized);
        } catch {}
    };

    const handleExport = () => {
        if (!canExport) return;
        setExporting(true);
        setProgress('Preparing export…');

        const params =
            period === 'yearly'
                ? {
                      period,
                      year: new Date(yearDate).getFullYear(),
                      email: email.trim()
                  }
                : {
                      period,
                      year: new Date(monthDate).getFullYear(),
                      month: new Date(monthDate).getMonth() + 1,
                      email: email.trim(),
                      branch
                  };

        exportTimesheetXlsx({
            ...params,
            onProgress: setProgress,
            onError: (message) => {
                dispatch(setSnack({ message, type: 'error' }));
                setProgress('');
            },
            onSuccess: () => {
                setProgress('');
                dispatch(
                    setSnack({
                        message: 'Attendance Excel downloaded.',
                        type: 'success'
                    })
                );
                onClose();
            }
        }).finally(() => setExporting(false));
    };

    return (
        <DialogWrapper
            open={open}
            onClose={handleClose}
            sx={{ textAlign: 'center' }}
        >
            <Typography variant='h6' sx={{ mb: 1 }}>
                Export Attendance
            </Typography>
            <Divider sx={{ mb: 1 }} />

            <Box sx={{ textAlign: 'left', px: 2 }}>
                <GroupLabel>Period</GroupLabel>
                <RadioGroup
                    row
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                >
                    <FormControlLabel
                        value='monthly'
                        control={<Radio size='small' />}
                        sx={{ mr: 4 }}
                        label={
                            <Box>
                                <Typography variant='body2'>
                                    Monthly
                                </Typography>
                                <Typography
                                    variant='caption'
                                    color='text.secondary'
                                >
                                    All employees for one month
                                </Typography>
                            </Box>
                        }
                    />
                    <FormControlLabel
                        value='yearly'
                        control={<Radio size='small' />}
                        label={
                            <Box>
                                <Typography variant='body2'>Yearly</Typography>
                                <Typography
                                    variant='caption'
                                    color='text.secondary'
                                >
                                    One employee for a whole year
                                </Typography>
                            </Box>
                        }
                    />
                </RadioGroup>

                <GroupLabel>
                    {period === 'monthly' ? 'Month' : 'Year'}
                </GroupLabel>
                {period === 'monthly' ? (
                    <TimesheetsMonthPicker
                        input={{
                            value: monthDate,
                            onChange: handleMonthChange
                        }}
                    />
                ) : (
                    <TimesheetsYearPicker
                        input={{
                            value: yearDate,
                            onChange: handleYearChange
                        }}
                    />
                )}

                {period === 'monthly' && (
                    <>
                        <GroupLabel>Branch (optional)</GroupLabel>
                        <SelectBranch
                            input={{
                                name: 'export-branch',
                                onChange: setBranch,
                                value: branch
                            }}
                        />
                    </>
                )}

                <GroupLabel>
                    Employee email{' '}
                    {emailRequired ? '(required)' : '(optional filter)'}
                </GroupLabel>
                <TextField
                    fullWidth
                    size='small'
                    placeholder='employee@echologyx.com'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />

                {exporting && progress && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 2
                        }}
                    >
                        <CircularProgress size={16} />
                        <Typography variant='caption' color='text.secondary'>
                            {progress}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Divider sx={{ mt: 2 }} />
            <StyledCardAction>
                <Button
                    onClick={handleClose}
                    color='tertiary'
                    disabled={exporting}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleExport}
                    loading={exporting}
                    disabled={!canExport}
                >
                    Export Excel
                </Button>
            </StyledCardAction>
        </DialogWrapper>
    );
};

export default TimesheetExportModal;
